import React, { useEffect, useState } from 'react';
import {
  NavLink,
  Outlet,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { BsArrowLeft, BsCheck } from 'react-icons/bs';
import { VscSave } from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import { useTranslation } from 'react-i18next';
import { getDashboardById, updateDashboard } from '../../actions/dashboards';

import styles from './edit-dashboard.module.scss';
import { DASHBOARD_POST, DASHBOARD_PUT } from '../../actions/types';
import { updateIndicator } from '../../actions/indicators';
import Loader from '../../components/Loader';

const EditDashboard = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    dashboard, isLoading, user,
  } = useSelector((store) => store.dashboardReducer);
  const { indicators } = useSelector((store) => store.indicatorReducer);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm();
  const data = watch();

  // Centralized draft state for all indicator changes (visibility, size)
  // Structure: { [indicatorId]: { isPublished, size } }
  const [draftIndicatorChanges, setDraftIndicatorChanges] = useState({});

  useEffect(() => {
    if (!params.dashboardId) return;
    getDashboardById(dispatch, params.dashboardId);
  }, []);

  // Save all draft changes to the backend
  const saveDraftChanges = async () => {
    if (Object.keys(draftIndicatorChanges).length === 0) return;

    const promises = [];

    // Process each draft change
    Object.entries(draftIndicatorChanges).forEach(([id, changes]) => {
      // Prepare update data for this indicator (excluding position as it is handled separately)
      const updateData = {};
      if (changes.isPublished !== undefined) updateData.isPublished = changes.isPublished;
      if (changes.size !== undefined) updateData.size = changes.size;

      // Update indicator if there are changes
      if (Object.keys(updateData).length > 0) {
        promises.push(updateIndicator(dispatch, updateData, id));
      }
    });

    await Promise.all(promises);
    setDraftIndicatorChanges({});
  };

  async function submit() {
    // Save all draft indicator changes first
    await saveDraftChanges();
    // Then save dashboard data
    const payload = { ...data };
    // Find details on the current user
    if (user?.user?.firstName?.value && user?.user?.lastName?.value) {
      const editorName = ''.concat(user.user.firstName.value, ' ', user.user.lastName.value);
      payload.lastEditedBy = editorName;
    }
    delete payload.indicators;
    updateDashboard(dispatch, payload, params.dashboardId);
  }

  // Check if there are any draft changes
  const hasDraftChanges = Object.keys(draftIndicatorChanges).length > 0;
  // const api = watch('api');
  // const dataset = watch('dataset');
  useEffect(() => {
    if (dashboard && user) {
      const dashboardData = {
        ...dashboard,
        api: user.apis.find((a) => a.value === dashboard.requests),
        dataset: user.keys.find((a) => a.value === dashboard.key),
      };
      reset(dashboardData);
    }
  }, [dashboard, user]);

  // Get effective indicators with draft changes applied
  const getEffectiveIndicators = () => {
    if (Object.keys(draftIndicatorChanges).length === 0) return indicators;

    return indicators.map((indicator) => {
      if (draftIndicatorChanges[indicator._id]) {
        return { ...indicator, ...draftIndicatorChanges[indicator._id] };
      }
      return indicator;
    });
  };

  const effectiveIndicators = getEffectiveIndicators();

  return (
    <div className={styles.editDashboard}>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.backward} onClick={() => navigate(`/dashboard-list/${search}`)}><BsArrowLeft /> {t('btn.back')}</p>
          <div>
            <h1>{dashboard?.name}</h1>
            <div className={styles.info}>
              <p>{t('label.editor')} : {dashboard?.editor}</p>
              <p>{t('label.editorMail')} : {dashboard?.email}</p>
            </div>
          </div>
          <div className={styles.containerButton}>
            <button
              className='secondary'
              onClick={() => navigate(`/preview-dashboard/${params.dashboardId}/${search}`)}
            >{t('dashboard.preview')}
            </button>
            <button
              className={`${!isDirty && !hasDraftChanges ? styles.saved : ''}`}
              onClick={handleSubmit(submit)}
              disabled={!isDirty && !hasDraftChanges}
            >
              <span>{isDirty || hasDraftChanges ? t('btn.save') : t('btn.saved')}</span>
              {(isLoading.includes(DASHBOARD_POST) || isLoading.includes(DASHBOARD_PUT)) ? (
                <span className='icon loader'>
                  <Loader size={18} className='secondary' />
                </span>
              ) : (
                <span className='icon'>
                  {!isDirty && !hasDraftChanges ? <BsCheck size={20} /> : <VscSave />}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className={styles.content}>
          <nav>
            <NavLink to={`indicators${search}`} className={({ isActive }) => (isActive ? styles.active : undefined)} >{t('dashboard.choose')}</NavLink>
            <NavLink to={`layout${search}`} className={({ isActive }) => (isActive ? styles.active : undefined)} >{t('dashboard.layout')}</NavLink>
            {dashboard?.state
              ? <NavLink to={`embed/share${search}`} className={({ isActive }) => (isActive || pathname.includes('embed') ? styles.active : undefined)} >{t('dashboard.integration')}</NavLink>
              : <a className={styles.disabled}>{t('dashboard.integration')}</a>
            }
          </nav>
          <div className={styles.tabs}>
            <Outlet context={{
              control,
              errors,
              handleSubmit,
              watch,
              setValue,
              effectiveIndicators,
              setDraftIndicatorChanges,
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDashboard;
